import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Finished requests live in the Done table below the open ones (K2-10).
const FINISHED = ["done_not_in_app", "done", "exists"]

// The company's Feedback list (Phelipe 2026-09-23): type chips + status +
// search filter the server-rendered rows, a row opens the details panel
// (feedback-detail, in the layout since 2026-10-17, so the ConsLog chat window
// can host it too) with the problem / solution / files / history and the
// comments, and the priority column PATCHes. The panel tells this list what
// changed by event (feedback:request-updated / comment-added / request-deleted /
// priority-changed). Everything updates in place — no page reloads.
export default class extends Controller {
  static targets = ["row", "rows", "kindChip", "status", "search", "counts", "noMatch", "trashModal", "trashList", "trashEmpty", "trashCount",
    "openEmpty", "openCount", "doneBox", "doneToggle", "doneBody", "doneRows", "doneCount",
    "recentBox", "recentToggle", "recentBody", "recentRows", "recentCount",
    // "Notify me" standing rule (2026-09-25)
    "subBtn", "subBtnLabel", "subModal", "subKindsAll", "subKindsPick", "subKindBox", "subKind",
    "subWhoAll", "subWhoPick", "subUserBox", "subUser", "subFreqDaily", "subFreqInstant", "subOff"]
  static values = { baseUrl: String, kind: String, highlight: Number, i18n: Object }

  connect() {
    this._onKey = (e) => {
      if (e.key !== "Escape") return
      // An open Observation note closes first; then a pop-up. The details panel handles its own Escape.
      if (this._obsTip && !this._obsTip.hidden) { this._obsPinned = null; return this._obsHide() }
      if (this.hasSubModalTarget && !this.subModalTarget.classList.contains("hidden")) return this.closeSubscribe()
      if (this.hasTrashModalTarget && !this.trashModalTarget.classList.contains("hidden")) return this.closeTrash()
    }
    document.addEventListener("keydown", this._onKey)
    // A tapped Observation note closes with a tap anywhere else, and on scroll.
    this._onObsAway = (e) => { if (this._obsPinned && !this._obsPinned.contains(e.target)) { this._obsPinned = null; this._obsHide() } }
    this._onObsScroll = () => { this._obsPinned = null; this._obsHide() }
    document.addEventListener("click", this._onObsAway)
    window.addEventListener("scroll", this._onObsScroll, true)
    // The instant-feedback panel outlives the page: when a recording that was
    // still uploading at Send lands (or the AI writes it up), it tells us.
    this._onUpdate = (e) => {
      this._applyUpdate(e.detail?.request)
      // The details panel sends the row the server redrew after an edit or a reopen.
      if (e.detail?.row_html && e.detail?.request?.id) this._swapRow(e.detail.row_html, e.detail.request.id)
    }
    window.addEventListener("feedback:request-updated", this._onUpdate)
    this.kindChipTargets.forEach(c => c.dataset.on = String(c.dataset.kind === (this.kindValue || "")))
    this.filter()
    if (this.highlightValue) {
      const row = this.rowTargets.find(r => +r.dataset.id === this.highlightValue)
      // A link to one request (the "it's done" email) shows it even when the
      // saved Status filter would hide it — for this visit only.
      if (row?.classList.contains("hidden") && this.statusTarget.value) { this.statusTarget.value = ""; this.filter() }
      if (row && this.hasDoneRowsTarget && this.doneRowsTarget.contains(row)) this._setDoneOpen(true)
      if (row && this.hasRecentRowsTarget && this.recentRowsTarget.contains(row)) this._setRecentOpen(true)
      if (row) { row.classList.add("bg-yellow-50"); row.scrollIntoView({ block: "center" }) }
      // "Open it in ConsLog" in a follow-up email (Phelipe 2026-10-09): straight into the request, answer box ready.
      if (row && new URLSearchParams(location.search).get("answer")) { this._focusComment = true; row.click() }
    }
    // A comment landing on a request (from the details panel or the recorder): the row's count and its Question tag.
    this._onCommentAdded = (e) => { const d = e.detail; if (!d?.id) return; this._bumpCommentChip(d.id, d.comment_count); if (!d.question_pending) this._rowAnswered(d.id) }
    window.addEventListener("feedback:comment-added", this._onCommentAdded)
    // Deleted from the details panel: the row goes, the trash grows.
    this._onDeleted = (e) => this._removeRow(e.detail)
    window.addEventListener("feedback:request-deleted", this._onDeleted)
    // The priority picked in the details panel paints the row's select too.
    this._onPrio = (e) => this._priorityChanged(e.detail)
    window.addEventListener("feedback:priority-changed", this._onPrio)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
    window.removeEventListener("feedback:request-updated", this._onUpdate)
    window.removeEventListener("feedback:comment-added", this._onCommentAdded)
    window.removeEventListener("feedback:request-deleted", this._onDeleted)
    window.removeEventListener("feedback:priority-changed", this._onPrio)
    document.removeEventListener("click", this._onObsAway)
    window.removeEventListener("scroll", this._onObsScroll, true)
    this._obsTip?.remove()
    this._obsTip = null
  }

  // ── The team's Observation (Phelipe 2026-10-07) ─────────────────────
  // A highlighted tag on the row; its note shows on hover or keyboard focus,
  // and on a tap for touch screens (a second tap, Escape or a tap elsewhere
  // closes it). A small white card with an amber "Note from the ConsLog team"
  // heading and an arrow at the label, fading in; one floating element fixed
  // to the window so the list's rounded box doesn't clip it.
  showObservation(e) { this._obsShow(e.currentTarget) }

  hideObservation() { if (!this._obsPinned) this._obsHide() }

  toggleObservation(e) {
    e.preventDefault()
    e.stopPropagation()
    const el = e.currentTarget
    if (this._obsPinned === el) { this._obsPinned = null; this._obsHide(); return }
    this._obsPinned = el
    this._obsShow(el)
  }

  _obsShow(el) {
    const note = el.dataset.observation
    if (!note) return
    let tip = this._obsTip
    if (!tip) {
      // A small white card: an amber heading strip, the note, and an arrow at the label.
      tip = this._obsTip = document.createElement("div")
      tip.setAttribute("role", "tooltip")
      tip.dataset.observationNote = ""
      tip.className = "fixed z-50 w-max pointer-events-none opacity-0 translate-y-1 transition duration-150 ease-out"
      tip.innerHTML = `<div class="relative rounded-xl bg-white border border-gray-200 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.28)] overflow-hidden">
          <div class="flex items-center gap-1.5 px-3.5 pt-2.5 pb-2 bg-amber-50 border-b border-amber-100 text-amber-800 text-[12px] font-semibold uppercase tracking-wide">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 shrink-0" aria-hidden="true"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd"/></svg>
            <span data-obs-head></span>
          </div>
          <div class="px-3.5 py-2.5 text-[13.5px] leading-relaxed text-gray-700 whitespace-pre-line" data-obs-text></div>
        </div>
        <div class="absolute z-10 w-3 h-3 rotate-45 border-gray-200" data-obs-arrow></div>`
      document.body.appendChild(tip)
    }
    tip.querySelector("[data-obs-head]").textContent = this.i18nValue.note_from_team || "Note from the ConsLog team"
    tip.querySelector("[data-obs-text]").textContent = note
    tip.style.maxWidth = Math.min(340, window.innerWidth - 16) + "px"
    tip.classList.remove("opacity-100", "translate-y-0")
    tip.hidden = false
    const r = el.getBoundingClientRect(), w = tip.offsetWidth, h = tip.offsetHeight
    const left = Math.max(8, Math.min(r.left + r.width / 2 - 28, window.innerWidth - w - 8))
    const above = r.bottom + 10 + h > window.innerHeight - 8
    tip.style.left = left + "px"
    tip.style.top = (above ? Math.max(8, r.top - h - 10) : r.bottom + 10) + "px"
    // The arrow points at the label: on the card's top edge (amber, like the heading) or its bottom edge.
    const arrow = tip.querySelector("[data-obs-arrow]")
    arrow.style.left = Math.max(12, Math.min(r.left + r.width / 2 - left - 6, w - 24)) + "px"
    arrow.style.top = above ? "" : "-6px"
    arrow.style.bottom = above ? "-6px" : ""
    arrow.className = `absolute z-10 w-3 h-3 rotate-45 border-gray-200 ${above ? "bg-white border-r border-b" : "bg-amber-50 border-l border-t"}`
    void tip.offsetWidth // laid out hidden first, so the fade-in runs
    tip.classList.add("opacity-100", "translate-y-0")
  }

  _obsHide() {
    if (!this._obsTip) return
    this._obsTip.classList.remove("opacity-100", "translate-y-0")
    this._obsTip.hidden = true
  }

  // Repaint the row's files chips (and the drawer, if it's open on that row).
  _applyUpdate(r) {
    if (!r?.id) return
    const row = this.rowTargets.find(x => +x.dataset.id === +r.id)
    if (row) {
      const n = r.attachment_count || 0, pend = (r.pending_uploads || []).length
      const count = row.querySelector("[data-files-count]"), pending = row.querySelector("[data-files-pending]")
      // The hidden attribute (not the class): the class loses to inline-flex in Tailwind v4's output.
      if (count) { count.hidden = !n; const num = count.querySelector("[data-files-n]"); if (num) num.textContent = n }
      if (pending) pending.hidden = !pend
      if (r.comment_count !== undefined) this._bumpCommentChip(r.id, r.comment_count)
      const title = this._titleEl(row); if (title && r.title) title.textContent = r.title
      row.dataset.hay = [r.ref, r.title, r.area, r.problem, r.origin_label].join(" ").toLowerCase()
    }
  }

  // The row's title: [data-title], not the first bold text — the ID sits before it now (Phelipe 2026-09-24).
  _titleEl(row) { return row.querySelector("[data-title]") || row.querySelector(".font-semibold") }

  // ── filters ─────────────────────────────────────────────────────────
  setKind(e) {
    this.kindValue = e.currentTarget.dataset.kind
    this.kindChipTargets.forEach(c => c.dataset.on = String(c === e.currentTarget))
    this.filter()
  }

  clearFilters() {
    this.kindValue = ""
    this.kindChipTargets.forEach(c => c.dataset.on = String(c.dataset.kind === ""))
    this.statusTarget.value = ""
    this.searchTarget.value = ""
    this.filter()
    this._saveStatus()
  }

  // The Status filter is remembered per person (Phelipe 2026-09-24): the page
  // opens with the one they last picked.
  setStatus() {
    this.filter()
    this._saveStatus()
  }

  _saveStatus() {
    fetch(`${this.baseUrlValue}/save_filters`, { method: "POST", headers: this._headers(), body: JSON.stringify({ status: this.statusTarget.value }) }).catch(() => {})
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase(), st = this.statusTarget.value, kind = this.kindValue || ""
    let shown = 0, done = 0, active = 0, fresh = 0
    this.rowTargets.forEach(r => {
      const ok = (!kind || r.dataset.kind === kind) && (!st || r.dataset.status === st) && (!q || r.dataset.hay.includes(q))
      r.classList.toggle("hidden", !ok)
      if (!ok) return
      shown++
      if (r.dataset.status === "done" || r.dataset.status === "done_not_in_app" || r.dataset.status === "exists") done++
      else if (r.dataset.status === "new") fresh++
      else active++
    })
    this.noMatchTarget.classList.toggle("hidden", !(shown === 0 && this.rowTargets.length > 0))
    this._paintDone(st)
    // The "still open" count above the list follows the filters (Phelipe 2026-10-08).
    if (this.hasOpenCountTarget) {
      const open = this.rowsTarget.querySelectorAll('[data-feedback-list-target="row"]:not(.hidden)').length
      this.openCountTarget.textContent = (open === 1 ? this.i18nValue.open_count_one : this.i18nValue.open_count_other).replace("%{count}", open)
      this.openCountTarget.hidden = this.rowTargets.length === 0
    }
    if (this.hasCountsTarget && this.rowTargets.length) {
      this.countsTarget.textContent = this.i18nValue.counts
        .replace("%{shown}", shown).replace("%{total}", this.rowTargets.length)
        .replace("%{done}", done).replace("%{active}", active).replace("%{fresh}", fresh)
    }
  }

  // ── add ─────────────────────────────────────────────────────────────
  // Opens the instant-feedback panel on its first screen — draw, record or
  // write (Phelipe 2026-09-23), not straight into the write-only form.
  addRequest() { window.dispatchEvent(new CustomEvent("instant-feedback:open")) }

  stop(e) { e.stopPropagation() }

  // ── priority (the customer's own column) ────────────────────────────
  async setPriority(e) {
    const sel = e.currentTarget, id = sel.dataset.prioFor, value = sel.value
    this._paintPrio(sel, value)
    const res = await fetch(`${this.baseUrlValue}/${id}`, {
      method: "PATCH", headers: this._headers(), body: JSON.stringify({ customer_priority: value })
    }).catch(() => null)
    if (!res || !res.ok) { this._toast(this.i18nValue.priority_cleared, "alert"); return }
    const label = value ? this.i18nValue.priorities[value] : ""
    this._toast(value ? this.i18nValue.priority_saved.replace("%{priority}", label) : this.i18nValue.priority_cleared)
    // The details panel, if it shows this request, paints its select the same.
    window.dispatchEvent(new CustomEvent("feedback:priority-changed", { detail: { id: +id, value, from: "list" } }))
  }

  _paintPrio(sel, value) {
    const cls = this.i18nValue.prio_cls
    Object.values(cls).forEach(c => c.split(" ").forEach(k => sel.classList.remove(k)))
    if (cls[value]) cls[value].split(" ").forEach(k => sel.classList.add(k))
  }

  // ── drawer ──────────────────────────────────────────────────────────
  keyOpen(e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.open(e) } }

  open(e) {
    if (e.target.closest("select, input, label, a, button")) return
    const row = e.currentTarget, id = +row.dataset.id
    // A draft isn't sent yet: it reopens in the instant-feedback panel to be
    // finished (Phelipe 2026-09-23), not in the details panel.
    if (row.dataset.draft) { window.dispatchEvent(new CustomEvent("instant-feedback:resume", { detail: { id } })); return }
    // The details panel (feedback-detail, in the layout) opens it.
    const focus = this._focusComment
    this._focusComment = false
    window.dispatchEvent(new CustomEvent("feedback-detail:open", { detail: { id, title: this._titleEl(row)?.textContent || "", focus_comment: focus } }))
  }

  // The list row, redrawn by the server after an edit, in the same spot.
  _swapRow(html, id) {
    const old = this.rowTargets.find(x => +x.dataset.id === +id)
    if (!old || !html) return
    const tpl = document.createElement("template")
    tpl.innerHTML = html.trim()
    const row = tpl.content.firstElementChild
    if (!row) return
    // A reopened request goes back up to the open ones (and a finished one down to Done), first in line.
    const home = this._homeFor(row)
    if (home.contains(old)) old.replaceWith(row)
    else { old.remove(); home.prepend(row) }
    this.filter()
  }

  // ── the Done table (K2-10, Phelipe 2026-09-24) ──────────────────────
  // Finished requests sit below the open ones, folded until opened. Picking a
  // finished status in the filter opens it; so does a link to a finished one.
  toggleDone() { this._setDoneOpen(this.doneBodyTarget.hidden) }

  // Where a row belongs: open, Done within the last 7 business days (data-recent, Phelipe 2026-09-25), or Done.
  _homeFor(row) {
    if (!FINISHED.includes(row.dataset.status)) return this.rowsTarget
    if (row.dataset.recent && this.hasRecentRowsTarget) return this.recentRowsTarget
    return this.hasDoneRowsTarget ? this.doneRowsTarget : this.rowsTarget
  }

  toggleRecent() { this._setRecentOpen(this.recentBodyTarget.hidden) }

  _setRecentOpen(open) {
    if (!this.hasRecentBodyTarget) return
    this.recentBodyTarget.hidden = !open
    this.recentToggleTarget.setAttribute("aria-expanded", String(open))
    this.recentToggleTarget.querySelector("[data-recent-chevron]")?.classList.toggle("rotate-90", open)
  }

  _setDoneOpen(open) {
    if (!this.hasDoneBodyTarget) return
    this.doneBodyTarget.hidden = !open
    this.doneToggleTarget.setAttribute("aria-expanded", String(open))
    this.doneToggleTarget.querySelector("[data-done-chevron]")?.classList.toggle("rotate-90", open)
  }

  // The Done count follows the filters; the table hides when none match, and
  // the open table says so when only finished ones are left.
  _paintDone(st) {
    if (!this.hasDoneRowsTarget) return
    const visible = (box) => [...box.querySelectorAll('[data-feedback-list-target="row"]')].filter(r => !r.classList.contains("hidden")).length
    const open = visible(this.rowsTarget), done = visible(this.doneRowsTarget)
    const recent = this.hasRecentRowsTarget ? visible(this.recentRowsTarget) : 0
    this.doneCountTarget.textContent = done
    this.doneBoxTarget.hidden = done === 0
    // The recent table only shows when something finished in the last 7 business days (Phelipe 2026-09-25).
    if (this.hasRecentBoxTarget) { this.recentCountTarget.textContent = recent; this.recentBoxTarget.hidden = recent === 0 }
    if (this.hasOpenEmptyTarget) this.openEmptyTarget.hidden = !(open === 0 && done + recent > 0)
    if (FINISHED.includes(st) && this._doneFor !== st) { this._setDoneOpen(true); this._setRecentOpen(true) }
    this._doneFor = st
  }

  _bumpCommentChip(id, n) {
    const row = this.rowTargets.find(x => +x.dataset.id === +id)
    const chip = row?.querySelector("[data-comments-count]")
    if (!chip) return
    chip.hidden = !n
    const num = chip.querySelector("[data-comments-n]")
    if (num) num.textContent = n
  }

  // The row's "Question pending" tag goes once an answer has landed.
  _rowAnswered(id) {
    const row = this.rowTargets.find(x => +x.dataset.id === +id) || this.element.querySelector(`[data-feedback-review-target="row"][data-id="${id}"]`)
    const tag = row?.querySelector("[data-question-pending]")
    if (tag) tag.hidden = true
  }

  // Changed in the details panel: the row's select follows.
  _priorityChanged({ id, value, from } = {}) {
    if (from === "list" || !id) return
    const row = this.rowTargets.find(x => +x.dataset.id === +id)
    const sel = row?.querySelector("[data-prio-for]")
    if (sel && sel.value !== value) { sel.value = value; this._paintPrio(sel, value) }
  }

  // ── deleted from the details panel → the row goes, the trash grows ──
  _removeRow({ id, trash_html } = {}) {
    if (!id) return
    this.rowTargets.find(r => +r.dataset.id === +id)?.remove()
    if (!this.rowTargets.length && !this.rowsTarget.querySelector("[data-empty]")) {
      this.rowsTarget.insertAdjacentHTML("beforeend", `<div class="px-6 py-10 text-center text-[13.5px] text-text-muted" data-empty>${this._esc(this.i18nValue.empty)}</div>`)
    }
    this.filter()
    if (trash_html && this.hasTrashListTarget) { this.trashListTarget.insertAdjacentHTML("afterbegin", trash_html); this.trashEmptyTarget.classList.add("hidden") }
    this._bumpTrash(1)
  }

  // ── "Notify me": one standing rule for the whole list ───────────────
  openSubscribe() { this.subModalTarget.classList.remove("hidden") }
  closeSubscribe() { if (this.hasSubModalTarget) this.subModalTarget.classList.add("hidden") }

  // "Only these" / "Only these people" reveal their pickers; "All" hides them.
  subScopeChanged() {
    if (this.hasSubKindBoxTarget) this.subKindBoxTarget.classList.toggle("hidden", !this.subKindsPickTarget.checked)
    if (this.hasSubUserBoxTarget) this.subUserBoxTarget.classList.toggle("hidden", !this.subWhoPickTarget.checked)
  }

  async saveSubscribe() {
    const t = this.i18nValue
    // "All" saves an empty list, which the server reads as "no limit".
    const kinds = this.subKindsPickTarget.checked ? this.subKindTargets.filter(c => c.checked).map(c => c.value) : []
    const requesterIds = this.subWhoPickTarget.checked ? this.subUserTargets.filter(c => c.checked).map(c => c.value) : []
    // A picker turned on but left empty would quietly mean "everything" —
    // say so instead of saving something they didn't mean.
    if (this.subKindsPickTarget.checked && !kinds.length) return this._toast(t.sub_pick_kind, "alert")
    if (this.subWhoPickTarget.checked && !requesterIds.length) return this._toast(t.sub_pick_person, "alert")

    const frequency = this.subFreqInstantTarget.checked ? "instant" : "daily"
    const res = await fetch(`${this.baseUrlValue}/save_subscription`, {
      method: "POST", headers: this._headers(),
      body: JSON.stringify({ kinds, requester_ids: requesterIds, frequency })
    }).catch(() => null)
    if (!res || !res.ok) return this._toast(t.sub_failed, "alert")

    this._paintSubBtn(true)
    this.closeSubscribe()
    this._toast(frequency === "instant" ? t.sub_saved_instant : t.sub_saved_daily)
  }

  async turnOffSubscribe() {
    const t = this.i18nValue
    const res = await fetch(`${this.baseUrlValue}/unsubscribe`, { method: "DELETE", headers: this._headers() }).catch(() => null)
    if (!res || !res.ok) return this._toast(t.sub_failed, "alert")

    this._paintSubBtn(false)
    this.closeSubscribe()
    this._toast(t.sub_off)
  }

  _paintSubBtn(on) {
    const t = this.i18nValue
    if (this.hasSubBtnTarget) this.subBtnTarget.dataset.on = String(on)
    if (this.hasSubBtnLabelTarget) this.subBtnLabelTarget.textContent = on ? t.sub_on_short : t.sub_notify_me
    if (this.hasSubOffTarget) this.subOffTarget.classList.toggle("hidden", !on)
  }

  openTrash() { this.trashModalTarget.classList.remove("hidden") }
  closeTrash() { if (this.hasTrashModalTarget) this.trashModalTarget.classList.add("hidden") }

  async restore(e) {
    const btn = e.currentTarget, id = btn.dataset.id, t = this.i18nValue
    btn.disabled = true
    const res = await fetch(`${this.baseUrlValue}/${id}/restore`, { method: "POST", headers: this._headers() }).catch(() => null)
    const data = res && res.ok ? await res.json().catch(() => null) : null
    if (!data) { btn.disabled = false; this._toast(t.delete_failed, "alert"); return }
    btn.closest("[data-trash-id]")?.remove()
    this._bumpTrash(-1)
    if (!this.trashListTarget.querySelector("[data-trash-id]")) this.trashEmptyTarget.classList.remove("hidden")
    if (data.row_html) {
      // Back on top of the list (it was just touched), with a short highlight.
      this.rowsTarget.querySelector("[data-empty]")?.remove()
      const tpl = document.createElement("template")
      tpl.innerHTML = data.row_html.trim()
      const row = tpl.content.firstElementChild
      if (row) {
        // A finished one goes back into the Done table (K2-10), or the recent one.
        const home = this._homeFor(row)
        home.prepend(row)
        row.classList.add("bg-yellow-50"); setTimeout(() => row.classList.remove("bg-yellow-50"), 3000)
      }
    }
    this.filter()
    this._toast(t.restored_toast)
  }

  _bumpTrash(delta) {
    if (!this.hasTrashCountTarget) return
    this.trashCountTarget.textContent = Math.max(0, (parseInt(this.trashCountTarget.textContent, 10) || 0) + delta)
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  _headers() {
    return { "Content-Type": "application/json", Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "4000")
    el.textContent = message
    document.body.prepend(el)
  }
}
